export interface User {
  user_id: number;
  email: string;
  name: string;
  created_at: string;
}

export interface Site {
  site_id: number;
  name: string;
  flux_repo_url: string | null;
  is_production: boolean;
  created_at: string;
  scan_error: string | null;
  scan_at: string | null;
  scan_error_at: string | null;
  scan_errors: string; // Aggregated scan errors from site + bots
}

export interface Artifact {
  artifact_id: number;
  app: string;
  image: string;
  tag: string | null;
  version: string;
  ci_url: string | null;
  pr_url: string | null;
  git_hash: string | null;
  branch: string | null;
  git_describe: string | null;
  created_at: string;
}

export interface Pin {
  pin_id: number;
  site_id: number;
  bot_id: number | null;
  app: string;
  branch: string;
  version: string;
  created_at: string;
  updated_at: string;
}

export interface Release {
  release_id: number;
  site_id: number;
  user_id: number | null;
  created_at: string;
  artifacts: Artifact[];
}

export interface Bot {
  bot_id: number;
  site_id: number;
  name: string;
  created_at: string;
  scan_error: string | null;
  scan_at: string | null;
  scan_error_at: string | null;
}

export interface BotWithState extends Bot {
  current_versions: Record<string, string>;
  current_artifacts: Record<string, Artifact>;
  pending_artifact: Record<string, Artifact>;
}

export interface SiteWithState extends Site {
  pins: Pin[];
  bots: BotWithState[];
  current_versions: Record<string, string>;
  current_artifacts: Record<string, Artifact>;
  pending_artifact: Record<string, Artifact>;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(error.error || "Request failed");
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json();
}

export const api = {
  getMe: () => request<User>("/api/auth/me"),
  logout: () => request<void>("/api/auth/logout", { method: "POST" }),

  listSites: () => request<Site[]>("/api/sites"),
  getSite: (id: number) => request<SiteWithState>(`/api/sites/${id}`),
  createSite: (data: {
    name: string;
    kubeconfig?: string;
    flux_repo_url?: string;
    is_production: boolean;
  }) =>
    request<Site>("/api/sites", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  updateSite: (
    id: number,
    data: {
      name: string;
      kubeconfig?: string;
      flux_repo_url?: string;
      is_production: boolean;
    },
  ) =>
    request<Site>(`/api/sites/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  deleteSite: (id: number) =>
    request<void>(`/api/sites/${id}`, { method: "DELETE" }),

  listArtifacts: (app?: string, branch?: string, limit?: number) => {
    const params = new URLSearchParams();
    if (app) params.set("app", app);
    if (branch) params.set("branch", branch);
    if (limit) params.set("limit", String(limit));
    return request<Artifact[]>(`/api/artifacts?${params}`);
  },
  listAppNames: () => request<string[]>("/api/app-names"),
  listBranches: (app: string) =>
    request<string[]>(`/api/branches?app=${encodeURIComponent(app)}`),
  listVersions: (app: string, branch: string) =>
    request<string[]>(
      `/api/versions?app=${encodeURIComponent(app)}&branch=${encodeURIComponent(branch)}`,
    ),

  listBots: (siteId: number) => request<Bot[]>(`/api/sites/${siteId}/bots`),
  createBot: (siteId: number, data: { name: string; kubeconfig: string }) =>
    request<Bot>(`/api/sites/${siteId}/bots`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  updateBot: (
    siteId: number,
    botId: number,
    data: { name: string; kubeconfig: string },
  ) =>
    request<Bot>(`/api/sites/${siteId}/bots/${botId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  deleteBot: (siteId: number, botId: number) =>
    request<void>(`/api/sites/${siteId}/bots/${botId}`, { method: "DELETE" }),

  listPins: (siteId: number) => request<Pin[]>(`/api/sites/${siteId}/pins`),
  upsertPin: (
    siteId: number,
    app: string,
    botId: number | null,
    branch?: string | null,
    version?: string | null,
    pinId?: number | null,
  ) =>
    request<Pin>(`/api/sites/${siteId}/pins`, {
      method: "PUT",
      body: JSON.stringify({
        pin_id: pinId ?? undefined,
        app,
        bot_id: botId,
        branch,
        version,
      }),
    }),
  deletePin: (siteId: number, app: string, botId: number | null) => {
    const params = botId !== null ? `?bot_id=${botId}` : "";
    return request<void>(`/api/sites/${siteId}/pins/${app}${params}`, {
      method: "DELETE",
    });
  },

  listReleases: (siteId: number, limit?: number) => {
    const params = new URLSearchParams();
    if (limit) params.set("limit", String(limit));
    return request<Release[]>(`/api/sites/${siteId}/releases?${params}`);
  },
  deploy: (siteId: number, artifactIds: number[]) =>
    request<Release>(`/api/sites/${siteId}/deploy`, {
      method: "POST",
      body: JSON.stringify({ artifact_ids: artifactIds }),
    }),
};
